import { findRecord } from '@warp-drive/utilities/json-api';
import { warn } from '@ember/debug';
import { actorName, isAuditDoerHash } from 'tio-data/utils/audit-doer';

import type { Store } from '@warp-drive/core';
import type { UserExtension as UserModel } from 'tio-data/data/resources/user.ext';

// Audit doer values only hold a user FK for authenticated changes against a legacy
// integer column; system/seed/import actions leave a non-numeric string (e.g.
// "demo-user") — neither of those resolves to GET /users/:id.
export function isResolvableUserId(value: unknown): value is string | number {
  if (typeof value === 'number') return Number.isInteger(value);
  return typeof value === 'string' && /^\d+$/.test(value);
}

// Non-numeric values return as-is with NO request issued — a GET
// /users/demo-user would 404 and surface as an unhandled promise rejection
// under WarpDrive.
export async function resolveAuditUserName(store: Store, value: unknown): Promise<string> {
  // A jsonb doer column is polymorphic (tio-api's JsonbDoer: Integer | String | Hash).
  // The Hash is the same `Auth::BaseDoer#paper_trail_data` snapshot PaperTrail records as
  // `whodunnit`, so it goes through the same parser the time machine uses — and without a
  // lookup: it already carries the actor's name as recorded, so fetching would cost a round
  // trip per row AND show today's name for a historical action. Comes back '' for the `{}`
  // column default and for a system actor with no service name, so the caller localizes.
  if (isAuditDoerHash(value)) {
    return actorName(value);
  }

  if (!isResolvableUserId(value)) {
    return typeof value === 'string' ? value : '';
  }

  try {
    const { content } = (await store.request(
      findRecord<UserModel>('user', String(value), { include: 'person' })
    )) as { content: { data: UserModel } };
    const person = content.data?.person;
    return person ? `${person.firstName} ${person.lastName}` : '';
  } catch (error) {
    // Only a 404 (since-deleted user) falls back to the raw id; other failures
    // (5xx, connectivity) are real errors we must not silently mask as a name.
    if ((error as { status?: number })?.status === 404) {
      return String(value);
    }
    warn(
      `resolve-audit-user: failed to resolve user "${String(value)}" ` +
        `(status ${(error as { status?: number })?.status ?? 'unknown'})`,
      false,
      { id: 'tio-common.resolve-audit-user.request-failed' }
    );
    return '';
  }
}
